import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import CompanyCard from "@/components/company/CompanyCard";
import CompanyList from "@/components/company/CompanyList";
import FilterSidebar from "@/components/company/FilterSidebar";
import ActiveFilterChips from "@/components/company/ActiveFilterChips";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, SquaresFour, ListBullets } from "@phosphor-icons/react";

const SORT_OPTIONS = [
  { value: "recently_added", label: "Recently added" },
  { value: "highest_raise", label: "Highest raise" },
  { value: "lowest_raise", label: "Lowest raise" },
  { value: "alphabetical", label: "Alphabetical" },
  { value: "most_viewed", label: "Most viewed" },
];

const DEFAULT_FILTERS = {
  industry: [],
  stage: [],
  revenue_status: [],
  region: [],
  country: [],
  priority: [],
  scheme: null,
  min_raise: 50000,
  max_raise: 20000000,
};

export default function Dashboard() {
  const { isClient } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [options, setOptions] = useState({});
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recently_added");
  const [view, setView] = useState("card");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/companies/filters/options").then((r) => setOptions(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { sort };
    if (q) params.q = q;
    if (filters.industry.length) params.industry = filters.industry.join(",");
    if (filters.stage.length) params.stage = filters.stage.join(",");
    if (filters.revenue_status.length) params.revenue_status = filters.revenue_status.join(",");
    if (filters.region?.length) params.region = filters.region.join(",");
    if (filters.country.length) params.country = filters.country.join(",");
    if (filters.priority.length) params.priority = filters.priority.join(",");
    if (filters.scheme) params.scheme = filters.scheme;
    if (filters.min_raise > 50000) params.min_raise = filters.min_raise;
    if (filters.max_raise < 20000000) params.max_raise = filters.max_raise;

    const t = setTimeout(() => {
      api.get("/companies", { params }).then((r) => {
        setCompanies(r.data);
        setLoading(false);
      }).catch(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q, sort, filters]);

  const onToggleBookmark = (id, bookmarked) => {
    setCompanies((cs) => cs.map((c) => (c.id === id ? { ...c, is_bookmarked: bookmarked } : c)));
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
      {/* Hero header */}
      <div className="mb-10">
        {isClient ? (
          <>
            <div className="text-[11px] uppercase tracking-[0.2em] text-amber-700 dark:text-amber-400 font-bold mb-3">Client Portal</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-3xl">
              Your <span className="text-emerald-600 dark:text-emerald-400">raise</span>, in one place.
            </h1>
            <p className="mt-4 text-muted-foreground text-lg max-w-2xl leading-relaxed">
              Manage your company profile, upload documents and keep your data room current. Only Inventya&apos;s investment team and approved investors can view what you publish.
            </p>
          </>
        ) : (
          <>
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-400 font-bold mb-3">Live Deal Flow</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-3xl">
              Curated investment opportunities, <span className="text-emerald-600 dark:text-emerald-400">ready for investor consideration.</span>
            </h1>
            <p className="mt-4 text-muted-foreground text-lg max-w-2xl leading-relaxed">
              A private, invitation-only pipeline of high-growth companies, each pre-screened by the Inventya investment team.
            </p>
          </>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-10">
        {!isClient && (
          <FilterSidebar
            options={options}
            filters={filters}
            setFilters={setFilters}
            onReset={() => setFilters(DEFAULT_FILTERS)}
          />
        )}

        <div className="flex-1 min-w-0">
          <div className="mb-5 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="search-input"
                placeholder="Search by company name, industry, or keyword…"
                className="pl-9 h-11"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-[180px] h-11" data-testid="sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex bg-secondary rounded-md p-0.5">
                <button
                  onClick={() => setView("card")}
                  data-testid="view-card-btn"
                  className={`h-10 w-10 grid place-items-center rounded transition-colors ${view === "card" ? "bg-card shadow-sm" : "hover:bg-card/50"}`}
                  aria-label="Card view"
                >
                  <SquaresFour size={16} weight={view === "card" ? "bold" : "regular"} />
                </button>
                <button
                  onClick={() => setView("list")}
                  data-testid="view-list-btn"
                  className={`h-10 w-10 grid place-items-center rounded transition-colors ${view === "list" ? "bg-card shadow-sm" : "hover:bg-card/50"}`}
                  aria-label="List view"
                >
                  <ListBullets size={16} weight={view === "list" ? "bold" : "regular"} />
                </button>
              </div>
            </div>
          </div>

          <div className="mb-4 flex items-baseline justify-between">
            <div className="text-sm text-muted-foreground">
              <span className="num-display font-semibold text-foreground">{companies.length}</span> {companies.length === 1 ? "opportunity" : "opportunities"}
            </div>
          </div>

          <ActiveFilterChips
            filters={filters}
            setFilters={setFilters}
            onReset={() => setFilters(DEFAULT_FILTERS)}
          />

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-80 rounded-2xl border border-border bg-card animate-pulse" />)}
            </div>
          ) : companies.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-16 text-center text-muted-foreground">
              <div className="text-lg font-display font-bold text-foreground">No companies match your filters</div>
              <div className="mt-2 text-sm">Try broadening your search or clearing filters.</div>
            </div>
          ) : view === "card" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="companies-grid">
              {companies.map((c) => <CompanyCard key={c.id} company={c} onToggleBookmark={onToggleBookmark} />)}
            </div>
          ) : (
            <CompanyList companies={companies} />
          )}
        </div>
      </div>
    </div>
  );
}
